import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { VhIconComponent } from '../icon/icon.component';

/**
 * Debounced search input. Works as a ControlValueAccessor and also
 * emits `valueChange` for template-driven cases. Leading search icon,
 * trailing clear button that appears only when there's a value.
 */
@Component({
  selector: 'vh-search-input',
  standalone: true,
  imports: [FormsModule, VhIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VhSearchInputComponent),
      multi: true,
    },
  ],
  template: `
    <span class="vh-search__icon" aria-hidden="true">
      <vh-icon name="search" size="md" />
    </span>
    <input
      #input
      type="search"
      role="searchbox"
      [placeholder]="placeholder()"
      [disabled]="disabled()"
      [attr.aria-label]="ariaLabel() ?? placeholder()"
      [ngModel]="value()"
      (ngModelChange)="onModel($event)"
      (blur)="onTouched()"
    />
    @if (value()) {
      <button type="button" class="vh-search__clear" aria-label="Clear search" (click)="clear()">
        <vh-icon name="x" size="sm" />
      </button>
    }
  `,
  styleUrl: './search-input.component.scss',
})
export class VhSearchInputComponent implements ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly input$ = new Subject<string>();

  readonly placeholder = input<string>('Search products, vendors, or categories');
  readonly ariaLabel = input<string | null>(null);
  readonly debounceMs = input<number>(300);
  readonly autofocus = input<boolean>(false);

  readonly valueChange = output<string>();

  protected readonly value = signal<string>('');
  protected readonly disabled = signal<boolean>(false);
  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('input');

  private onChange: (v: string) => void = () => {
    /* replaced by registerOnChange */
  };
  protected onTouched: () => void = () => {
    /* replaced by registerOnTouched */
  };

  constructor() {
    this.input$
      .pipe(
        debounceTime(this.debounceMs() || 300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((v) => {
        this.onChange(v);
        this.valueChange.emit(v);
      });

    afterNextRender(() => {
      if (this.autofocus()) this.inputEl()?.nativeElement.focus();
    });
  }

  writeValue(value: string | null | undefined): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onModel(next: string): void {
    this.value.set(next);
    this.input$.next(next);
  }

  clear(): void {
    this.value.set('');
    this.onChange('');
    this.valueChange.emit('');
    this.inputEl()?.nativeElement.focus();
  }
}
